// PKCE 助手（RFC 7636）：为 Cognito Hosted UI 的 Authorization Code + PKCE 流
// 生成 code_verifier / code_challenge(S256) 与 state。
//
// 仅依赖 Web Crypto API（浏览器与 Node ≥18 均通过 globalThis.crypto 提供），
// 不引入第三方依赖；本模块为纯逻辑，便于单元测试。
//
// 设计依据：design.md「PWA 设计 → 认证（OIDC + PKCE）」、Components 3。

/** code_verifier 允许的最小长度（RFC 7636 §4.1）。 */
export const MIN_VERIFIER_LENGTH = 43;
/** code_verifier 允许的最大长度（RFC 7636 §4.1）。 */
export const MAX_VERIFIER_LENGTH = 128;

function getCrypto(): Crypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || typeof c.getRandomValues !== "function" || !c.subtle) {
    throw new Error("Web Crypto API 不可用：无法执行 PKCE");
  }
  return c;
}

/**
 * 将字节序列编码为 base64url（无填充），字符集仅含 [A-Za-z0-9-_]，
 * 满足 PKCE 对 code_verifier / code_challenge 的 unreserved 字符要求。
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  // 浏览器用 btoa；Node 环境 vitest 也提供全局 btoa（Node ≥16）。
  const base64 =
    typeof btoa === "function"
      ? btoa(binary)
      : Buffer.from(binary, "binary").toString("base64");
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * 生成密码学随机字节，并以 base64url 编码为字符串。
 * @param byteLength 随机字节数（默认 32 → base64url 约 43 字符）。
 */
export function randomUrlSafeString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  getCrypto().getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * 生成 PKCE code_verifier：长度落在 [43,128]、仅含 unreserved 字符。
 * 默认 32 字节随机 → base64url 后约 43 字符。
 */
export function generateCodeVerifier(): string {
  const verifier = randomUrlSafeString(32);
  if (verifier.length < MIN_VERIFIER_LENGTH) {
    // 理论上不会发生（32 字节 → 43 字符）；保底再补随机。
    return (verifier + randomUrlSafeString(32)).slice(0, MAX_VERIFIER_LENGTH);
  }
  return verifier.slice(0, MAX_VERIFIER_LENGTH);
}

/**
 * 由 code_verifier 计算 S256 challenge：base64url(SHA-256(verifier))。
 */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await getCrypto().subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** 生成 OAuth state（CSRF 防护），同样为 url-safe 随机串。 */
export function generateState(): string {
  return randomUrlSafeString(16);
}

/** 一次性产出 PKCE 三件套：verifier、challenge(S256)、state。 */
export interface PkceChallenge {
  readonly verifier: string;
  readonly challenge: string;
  readonly state: string;
}

export async function createPkceChallenge(): Promise<PkceChallenge> {
  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateState();
  return { verifier, challenge, state };
}
